import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Private Engagement",
  "Corporate Event",
  "Gala or Fundraiser",
  "Stage Show",
  "Wedding",
  "Other",
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: EVENT_TYPES[0],
    event_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Inquiry received. Christopher will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        event_type: EVENT_TYPES[0],
        event_date: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative pt-7 md:pt-10 pb-28 md:pb-36 bg-[#050505] border-t border-[#C5A059]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="overline">04 — Booking</span>
              <span className="gold-rule" />
            </div>
            <Reveal>
              <h2
                data-testid="booking-headline"
                className="font-display text-[#F5F5F0] text-4xl sm:text-5xl md:text-6xl leading-[1.05]"
              >
                Make it the
                <span className="font-italic-display text-[#C5A059]"> night </span>
                they keep talking about.
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-8 text-[#A1A1AA] leading-relaxed max-w-md">
                Tell me a little about your event. I personally read every
                inquiry and typically respond within two business days.
              </p>
            </Reveal>

            <Reveal delay={2}>
              <dl className="mt-12 space-y-6">
                <div>
                  <dt className="overline text-[10px]">Direct</dt>
                  <dd className="font-display text-[#F5F5F0] text-xl mt-1">
                    <a
                      href="mailto:chriscuriouscreations@gmail.com"
                      data-testid="booking-direct-email"
                      className="hover:text-[#C5A059] transition-colors break-all"
                    >
                      chriscuriouscreations@gmail.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="overline text-[10px]">Based In</dt>
                  <dd className="font-display text-[#F5F5F0] text-xl mt-1">
                    Durham, NC · Available Worldwide
                  </dd>
                </div>
                <div>
                  <dt className="overline text-[10px]">Lead Time</dt>
                  <dd className="font-display text-[#F5F5F0] text-xl mt-1">
                    4–8 weeks recommended
                  </dd>
                </div>
              </dl>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form
                data-testid="booking-form"
                onSubmit={submit}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
              >
                <div className="md:col-span-1">
                  <label className="cdp-label" htmlFor="bk-name">
                    Your Name
                  </label>
                  <input
                    id="bk-name"
                    data-testid="booking-name-input"
                    className="cdp-input"
                    placeholder="Eleanor Voss"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="cdp-label" htmlFor="bk-email">
                    Email
                  </label>
                  <input
                    id="bk-email"
                    data-testid="booking-email-input"
                    type="email"
                    className="cdp-input"
                    placeholder="eleanor@aurum.co"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="cdp-label" htmlFor="bk-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="bk-phone"
                    data-testid="booking-phone-input"
                    className="cdp-input"
                    placeholder="+1 555 0184"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="cdp-label" htmlFor="bk-date">
                    Preferred Date
                  </label>
                  <input
                    id="bk-date"
                    data-testid="booking-date-input"
                    type="date"
                    className="cdp-input"
                    value={form.event_date}
                    onChange={update("event_date")}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="cdp-label" htmlFor="bk-type">
                    Event Type
                  </label>
                  <select
                    id="bk-type"
                    data-testid="booking-type-select"
                    className="cdp-input bg-transparent"
                    value={form.event_type}
                    onChange={update("event_type")}
                  >
                    {EVENT_TYPES.map((t) => (
                      <option
                        key={t}
                        value={t}
                        className="bg-[#0F0F0F] text-[#F5F5F0]"
                      >
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="cdp-label" htmlFor="bk-msg">
                    Tell me about the evening
                  </label>
                  <textarea
                    id="bk-msg"
                    data-testid="booking-message-input"
                    className="cdp-input"
                    placeholder="Guest count, venue, mood you're after…"
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>
                <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  <button
                    data-testid="booking-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-gold"
                  >
                    {submitting ? "Sending…" : "Send Inquiry"}
                  </button>
                  <span className="text-[#A1A1AA] text-xs leading-relaxed">
                    By submitting you agree to be contacted regarding your
                    inquiry. No spam — ever.
                  </span>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
